import { MONTH_TRANSLATED } from '@/common/messages/dateMessages';
import dayjs, { type Dayjs } from 'dayjs';
import type { PanelMode } from 'rc-picker/lib/interface';
import { useMemo, type VFC } from 'react';
import { useIntl } from '@/components/formatted_message/FormattedMessage';
import styled, { css } from 'styled-components';

export type StyledDateCellProps = {
  isActive: boolean;
  isToday: boolean;
};

const StyledCell = styled.div<Pick<StyledDateCellProps, 'isActive'>>`
  &&& {
    position: relative !important;
    z-index: 1;

    :hover {
      color: ${({ theme }) => theme.color.semantic.primary.type.default} !important;
      background: ${({ theme }) => theme.color.semantic.primary.background.hover};
    }

    ${({ isActive, theme }) =>
      isActive &&
      css`
        background: ${theme.color.semantic.primary.background.default};
        color: ${theme.color.semantic.primary.type.default};
      `}
  }
`;

const StyledDayCell = styled.div<StyledDateCellProps>`
  // NOTE: DateCell styles differ from others, &&&& is used to override other styling
  &&&& {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 4px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.grey.grey3};
    line-height: normal;

    ${({ isToday, theme }) =>
      isToday &&
      css`
        border: 1px solid ${theme.color.grey.black};
      `}

    ${({ isActive, theme }) =>
      isActive &&
      css`
        color: ${theme.color.grey.white};
        background-color: ${theme.color.theme.primary};
      `}
  }
`;

export type DateCellProps = {
  dateValue: Dayjs;
  currentPickerDate: Dayjs | null;
  activeRange?: [Dayjs | undefined, Dayjs | undefined];
  panelMode: PanelMode;
};

export const DateCell: VFC<DateCellProps> = ({ dateValue, currentPickerDate, activeRange, panelMode }) => {
  const intl = useIntl();
  const isActive = dateValue.isSame(currentPickerDate, 'day');
  const isToday = dateValue.isSame(dayjs(), 'day');
  const [from, to] = activeRange ?? [];
  const isInActiveRange =
    (from &&
      to &&
      ((dateValue.isAfter(from, 'day') && dateValue.isBefore(to, 'day')) ||
        dateValue.isSame(from, 'day') ||
        dateValue.isSame(to, 'day'))) ??
    false;

  const value = useMemo(() => {
    if (panelMode === 'date') {
      return dateValue.date();
    }
    if (panelMode === 'month') {
      const month = dateValue.month();
      return intl.formatMessage(MONTH_TRANSLATED[month as keyof typeof MONTH_TRANSLATED]);
    }
    return dateValue.year();
  }, [panelMode, dateValue, intl]);

  return panelMode === 'date' ? (
    <StyledDayCell isActive={isActive || isInActiveRange} isToday={isToday}>
      {value}
    </StyledDayCell>
  ) : (
    <StyledCell isActive={isActive || isInActiveRange}>{value}</StyledCell>
  );
};
